import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Mail, MessageSquare, User, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { toPersianNumber } from '@/utils/persianNumber';
import { userServiceApi } from '@/services/api';

interface SupportTicketProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  email?: string;
  subject?: string;
  message?: string;
}

const SupportTicket = ({ isOpen, onClose }: SupportTicketProps) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [responseMessage, setResponseMessage] = useState('');

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Prevent scrolling on mobile devices
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '0';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'وارد کردن ایمیل الزامی است';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'لطفا ایمیل معتبر وارد کنید';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'وارد کردن موضوع الزامی است';
    } else if (formData.subject.trim().length < 3) {
      newErrors.subject = 'موضوع باید حداقل 3 کاراکتر باشد';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'وارد کردن پیام الزامی است';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'پیام باید حداقل 10 کاراکتر باشد';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setResponseMessage('');
    
    try {
      const response = await userServiceApi.post('/support/ticket', {
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      
      if (response.success) {
        setSubmitStatus('success');
        setResponseMessage((response.data as any)?.message || 'تیکت شما با موفقیت ارسال شد');
        
        // Reset form
        setFormData({ email: '', subject: '', message: '' });
        setErrors({});
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
          setResponseMessage('');
        }, 2000);
      } else {
        throw new Error(response.error || 'خطا در ارسال تیکت');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      
      // Handle different error formats
      let errorMessage = 'خطا در ارسال تیکت. لطفا دوباره تلاش کنید.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.detail?.message) {
        errorMessage = error.response.data.detail.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResponseMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      // Reset form after modal closes
      setTimeout(() => {
        setFormData({ email: '', subject: '', message: '' });
        setErrors({});
        setSubmitStatus('idle');
        setResponseMessage('');
      }, 300);
    }
  };

  return (
    <div
      aria-hidden={!isOpen}
      className={cn(
        'transition-opacity duration-200 ease-out',
        isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      )}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={handleClose}
      />

      {/* Modal - Centered and Compact */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className={cn(
            'w-full max-w-md max-h-[85vh] bg-white rounded-2xl flex flex-col shadow-xl overflow-hidden',
            'transition-[transform,opacity] duration-200 ease-out',
            isOpen ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-95 opacity-0'
          )}
          dir="rtl"
        >
            {/* Header - Mobile Optimized */}
            <div className="flex items-center justify-between p-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 font-persian">
                    پشتیبانی بوکا
                  </h2>
                </div>
              </div>
              
              <motion.button
                onClick={handleClose}
                disabled={isSubmitting}
                className={cn(
                  'w-7 h-7 rounded-lg flex items-center justify-center transition-colors',
                  isSubmitting 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
                whileTap={{ scale: 0.95 }}
              >
                <X className="w-3.5 h-3.5" />
              </motion.button>
            </div>

            {/* Success/Error Message */}
            <AnimatePresence>
              {submitStatus !== 'idle' && (
                <motion.div
                  className={cn(
                    'mx-3 mt-2 p-2 rounded-lg flex items-center gap-2',
                    submitStatus === 'success' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  )}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  {submitStatus === 'success' ? (
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                  )}
                  <p className={cn(
                    'font-persian text-xs',
                    submitStatus === 'success' ? 'text-green-800' : 'text-red-800'
                  )}>
                    {responseMessage}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form - Scrollable */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {/* Email Field */}
                <div className="space-y-1">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 font-persian">
                    <Mail className="w-3 h-3" />
                    ایمیل
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="example@email.com"
                    disabled={isSubmitting}
                    className={cn(
                      'w-full px-3 py-2 rounded-lg border transition-all duration-200',
                      'focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400 font-persian text-sm',
                      errors.email 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-200 bg-gray-50 hover:bg-white',
                      isSubmitting && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                  {errors.email && (
                    <motion.p
                      className="text-xs text-red-600 font-persian"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>

                {/* Subject Field */}
                <div className="space-y-1">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 font-persian">
                    <User className="w-3 h-3" />
                    موضوع
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="موضوع تیکت خود را وارد کنید"
                    disabled={isSubmitting}
                    className={cn(
                      'w-full px-3 py-2 rounded-lg border transition-all duration-200',
                      'focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400 font-persian text-sm',
                      errors.subject 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-200 bg-gray-50 hover:bg-white',
                      isSubmitting && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                  {errors.subject && (
                    <motion.p
                      className="text-xs text-red-600 font-persian"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.subject}
                    </motion.p>
                  )}
                </div>

                {/* Message Field */}
                <div className="space-y-1">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 font-persian">
                    <MessageSquare className="w-3 h-3" />
                    پیام و توضیحات
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="مشکل یا سوال خود را به طور کامل توضیح دهید..."
                    rows={3}
                    disabled={isSubmitting}
                    className={cn(
                      'w-full px-3 py-2 rounded-lg border transition-all duration-200 resize-none',
                      'focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400 font-persian text-sm',
                      errors.message 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-200 bg-gray-50 hover:bg-white',
                      isSubmitting && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                  <div className="flex justify-between items-center">
                    {errors.message ? (
                      <motion.p
                        className="text-xs text-red-600 font-persian"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {errors.message}
                      </motion.p>
                    ) : (
                      <div />
                    )}
                    <p className="text-xs text-gray-500 font-persian">
                      {toPersianNumber(formData.message.length)} / {toPersianNumber(2000)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer - Mobile Optimized */}
              <div className="p-3 border-t border-gray-100">
                <motion.button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success'}
                  className={cn(
                    'w-full py-2.5 rounded-lg font-semibold font-persian transition-all duration-200',
                    'flex items-center justify-center gap-2 text-sm',
                    isSubmitting || submitStatus === 'success'
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-500 text-white hover:bg-primary-600 shadow-lg hover:shadow-xl'
                  )}
                  whileTap={!isSubmitting && submitStatus !== 'success' ? { scale: 0.98 } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                      در حال ارسال...
                    </>
                  ) : submitStatus === 'success' ? (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" />
                      ارسال شد
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      ارسال تیکت
                    </>
                  )}
                </motion.button>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default SupportTicket;
